import {
  faExclamationTriangle, faLongArrowAltLeft,
  faLongArrowAltRight, faTrashAlt
} from '@fortawesome/free-solid-svg-icons'; // Replaced Ant Design icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// Removed Ant Design imports
import { API } from 'aws-amplify';
import { useState } from 'react'; // Added useState
import { useLocalization } from '../../../localization';
import { useGlobalState } from '../../../store';
import { accountIconMap } from '../../../utils/customIcons';
import { syncTypeText } from '../../../utils/words';
import ColorPicker from '../../syncs/components/ColorPicker'; // Assuming refactored
// Added CB component imports
import { CBButton, CBModal } from '../../core/components/cbd';
import { CBMenu, CBMenuItem } from '../../core/components/CBDropdown';
import { CBProgress } from '../../core/components/CBProgress';


const syncStatusMap = {
  synced: 'normal',
  syncing: 'normal',
  synced_error: 'exception',
  deleting: 'exception',
};

const SyncItem = ({ item, preview = false, deleteCallback }) => {
  const { t } = useLocalization();
  const [license] = useGlobalState('license'); // Keep license if needed for dataLayer

  // State for modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showForceDeleteModal, setShowForceDeleteModal] = useState(false);
  const [showResyncModal, setShowResyncModal] = useState(false);
  const [showRepairModal, setShowRepairModal] = useState(false);

  function resolveError() {
    var str = item.errorState || ''; // Ensure str is not null
    if (str.includes('Auth token refresh failed for')) {
      return <div>{t('organization.org_sync_error_user_account')}</div>;
    } else if (str.includes('timeout')) {
      return null; // Don't show timeout errors explicitly?
    } else if (str.includes("User")) {
      return <div>{t('organization.org_sync_error_user_account')}</div>;
    } else if (str.includes('rateLimitExceeded') || str.includes('usageLimits')) {
      return <div>{t('organization.org_sync_error_rate_limit')}</div>;
    } else if(item.status == "sync_conflict") {
      return <div>{t('organization.org_sync_error_conflict')}</div>;
    } else if(str.includes("Forbidden")){
      return <div>{t('organization.org_sync_error_forbidden')}</div>;
    } else if (str != '') {
      return (
        <div>
          {t('organization.org_sync_error_contact_support')}{' '}
          <a href="mailto:support@calendarbridge.com" className="text-blue-600 hover:underline">
            support@calendarbridge.com
          </a>
        </div>
      );
    } else if (item.status === 'deleting' || item.status === 'delete_error') {
        return <div>{t('organization.org_sync_status_deleting')}</div>;
    }
    return null; // Default return if no specific error matches
  }

  // --- Modal Handlers ---
  const handleDeleteConfirm = async () => {
    setShowDeleteModal(false);
    try {
      await API.del('bridge', '/bridge', {
        body: { sourceCalendarId: item.sourceCalendarId, destCalendarId: item.destCalendarId },
      });
      // dataLayer push (optional)
      deleteCallback(); // Refresh list
    } catch (error) {
        console.error("Delete failed:", error);
        alert("Failed to delete sync.");
    }
  };

  const handleForceDeleteConfirm = async () => {
    setShowForceDeleteModal(false);
     try {
      await API.del('bridge', '/bridge?force=true', {
        body: { sourceCalendarId: item.sourceCalendarId, destCalendarId: item.destCalendarId },
      });
      // dataLayer push (optional)
      deleteCallback(); // Refresh list
    } catch (error) {
      console.error("Force delete failed:", error);
      alert("Failed to force delete sync.");
    }
  };

   const handleResyncConfirm = async () => {
    setShowResyncModal(false);
    try {
      await API.del('bridge', '/bridge?resync=true', { // Using DEL with query param for resync? Verify API
        body: { sourceCalendarId: item.sourceCalendarId, destCalendarId: item.destCalendarId },
      });
      deleteCallback(); // Refresh list after resync trigger
    } catch (ex) {
      console.error("Resync failed:", ex);
      alert(t('organization.org_sync_resync_error_message', { date: new Date(item.resynced + 1000 * 60 * 60 * 8).toLocaleString() }));
    }
  };

  const sleep = (time) => new Promise((resolve) => setTimeout(resolve, time));

  const handleRepairConfirm = async () => {
      setShowRepairModal(false);
      try {
          await API.del('bridge', '/bridge?resync=true&bump=true', { // Using DEL with query param for repair? Verify API
              body: { sourceCalendarId: item.sourceCalendarId, destCalendarId: item.destCalendarId },
          });
          await sleep(3000); // Wait before refreshing
          deleteCallback(); // Refresh list
      } catch (ex) {
          console.error("Repair failed:", ex);
          // Optionally show a generic error or just refresh
          await sleep(3000); // Wait even on error before refresh?
          deleteCallback();
      }
  };

  // --- End Modal Handlers ---

  const handleClick = (key) => {
    if (key === '0') setShowForceDeleteModal(true);
    else if (key === '1') setShowDeleteModal(true);
    else if (key === '4') setShowResyncModal(true);
    else if (key === '5') setShowRepairModal(true);
  };

  // Replaced Ant Menu with CBMenu/CBMenuItem for Dropdown
  const menuItems = (
    <CBMenu onClick={({ key }) => handleClick(key)}>
      {item.status === 'delete_error' && (
        <CBMenuItem key="0" className="text-red-600" icon={<FontAwesomeIcon icon={faTrashAlt} className="mr-2"/>}>
          {t('organization.org_sync_force_delete_button')}
        </CBMenuItem>
      )}
      <CBMenuItem key="1" className="text-red-600" icon={<FontAwesomeIcon icon={faTrashAlt} className="mr-2"/>}>
        {t('common.delete_button')}
      </CBMenuItem>
      {(!item.twoWay && item.status !== 'syncing' && item.status !== 'deleting' && (item.status === 'synced' || item.status === 'synced_error')) && (
        <CBMenuItem key="4" icon={
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
            </svg>}>
          {t('organization.org_sync_resync_menu_button')}
        </CBMenuItem>
      )}
      {(item.lastSyncTime + (10 * 60 * 1000) < new Date().getTime()) && item.status === 'syncing' && (
        <CBMenuItem key="5" icon={
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-2">
               <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
            </svg>}>
          {t('organization.org_sync_repair_menu_button')}
        </CBMenuItem>
      )}
    </CBMenu>
  );

  // Function to render inline menu buttons (alternative to dropdown)
  function renderMenuButtons(){
    return (
      <div className="flex flex-wrap gap-2">
        {item.status === 'delete_error' && (
          <CBButton key="0" type="danger" size="small" onClick={() => handleClick("0")} icon={<FontAwesomeIcon icon={faTrashAlt} />}>
            {t('organization.org_sync_force_delete_button')}
          </CBButton>
        )}
        <CBButton key="1" type="danger" size="small" onClick={() => handleClick("1")} icon={<div className="ml-2"><FontAwesomeIcon icon={faTrashAlt} /></div>}>
          {t('common.delete_button')}
        </CBButton>
        {(!item.twoWay && item.status !== 'syncing' && item.status !== 'deleting' && (item.status === 'synced' || item.status === 'synced_error')) && (
          <CBButton key="4" type="ternary" size="small" onClick={() => handleClick("4")} icon={
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
              </svg>}>
            {t('organization.org_sync_resync_menu_button')}
          </CBButton>
        )}
        {(item.lastSyncTime + (10 * 60 * 1000) < new Date().getTime()) && item.status === 'syncing' && (
          <CBButton key="5" type="ternary" size="small" onClick={() => handleClick("5")} icon={
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                 <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
              </svg>}>
            {t('organization.org_sync_repair_menu_button')}
          </CBButton>
        )}
      </div>
    );
  }


  return (
    <>
      {/* Replaced Card with styled div */}
      <div className="border p-4 mb-8 relative break-words rounded shadow-sm">
        {/* Removed absolute positioned Dropdown */}
        {/* Replaced Row/Col with Flexbox */}
        <div className="flex flex-col md:flex-row items-center">
          {/* Source Column */}
          <div className="w-full md:w-1/3 text-center mb-4 md:mb-0">
            {/* Replaced Title with h4 */}
            <h4 className="text-md font-semibold truncate mb-1">{item.sourceCalendarName}</h4>
            {/* Replaced Text with span */}
            <span className="text-sm text-gray-700 break-all">
              {item.sourceCalendarAccountId}
              <FontAwesomeIcon
                icon={accountIconMap[item.sourceCalendarType]?.icon || 'calendar'} // Safe access
                className="mx-2 inline-block"
                color={accountIconMap[item.sourceCalendarType]?.color || '#888'} // Safe access
              />
            </span>
            {!preview && item.sourceColor !== 'default' && item.sourceColor !== 'same' && (
              <ColorPicker type={item.sourceCalendarType} color={item.sourceColor} preview={true} />
            )}
          </div>

          {/* Middle Column */}
          <div className="w-full md:w-1/3 text-center mb-4 md:mb-0 px-2">
            {item.twoWay && <FontAwesomeIcon icon={faLongArrowAltLeft} size="2x" className="text-black mx-1" />} {/* Adjusted size/color */}
            <FontAwesomeIcon icon={faLongArrowAltRight} size="2x" className="text-black mx-1" />
            <br />
            {/* Replaced Text with span */}
            <span className="text-xs text-gray-700 block mt-1">
              {syncTypeText(
                !preview && item.securityPolicy.includes('subject'),
                !preview && item.securityPolicy.includes('description'),
                !preview && item.securityPolicy.includes('location'),
                !preview && item.securityPolicy.includes('reminder'),
                !preview && item.securityPolicy.includes('private'),
                !preview && item.securityPolicy.includes('meeting'),
                !preview && item.securityPolicy.includes('contact')
              )}
              {!preview && (
                <div>
                  <span className="text-xs text-gray-700">
                    {t('sync.tag')}: {item.tag != 'default' ? item.tag : t('sync.tag_copy_placeholder')}
                  </span>
                  {!preview && item.matchColors && <div className="text-xs text-gray-700">{t('sync.match_source_colors')}</div>}
                </div>
              )}
              {!preview && item.status === 'synced' && <span className="block text-green-600">{t('sync.active_up_to_date')}</span>}
            </span>
             {/* Progress/Error moved here */}
             {!preview && item.status !== 'synced' && !item.errorState?.includes('timeout') && (
                <div className="flex flex-col items-center mt-2">
                  <CBProgress
                    type="circle"
                    percent={Math.round((item.syncProgress / 12) * 100)}
                    width={30}
                    strokeWidth={4}
                    status={syncStatusMap[item.status]}
                    showInfo={false}
                  />
                  {syncStatusMap[item.status] === 'exception' && (
                    <div className="text-xs text-red-600 mt-1">{resolveError()}</div>
                  )}
                </div>
              )}
          </div>

          {/* Destination Column */}
          <div className="w-full md:w-1/3 text-center">
            <h4 className="text-md font-semibold truncate mb-1">{item.destCalendarName}</h4>
            <span className="text-sm text-gray-700 break-all">
              {item.destCalendarAccountId}
              <FontAwesomeIcon
                icon={accountIconMap[item.destCalendarType]?.icon || 'calendar'} // Safe access
                className="mx-2 inline-block"
                color={accountIconMap[item.destCalendarType]?.color || '#888'} // Safe access
              />
              {!preview && item.destinationColor !== 'same' && item.destinationColor !== 'default' && (
                <ColorPicker type={item.destCalendarType} color={item.destinationColor} preview={true} />
              )}
            </span>
          </div>
        </div>

        {/* Action Buttons (alternative to dropdown) */}
        {!preview && (
            <div className="mt-4 pt-4 border-t flex justify-end">
                {renderMenuButtons()}
            </div>
        )}

      </div>

      {/* Modal Definitions */}
      <CBModal
        title={t('organization.org_sync_delete_confirm_title')} visible={showDeleteModal}
        onPositiveClick={handleDeleteConfirm} onCancelClick={() => setShowDeleteModal(false)}
        positiveLabel={t('common.delete_button')} negativeLabel={t('common.cancel_button')}
        positiveButtonProps={{ className: 'bg-red-600 hover:bg-red-700' }}
      >
        <div className="flex items-center"><p>Are you sure?</p></div>
      </CBModal>
      <CBModal
        title={t('organization.org_sync_force_delete_confirm_title')} visible={showForceDeleteModal}
        onPositiveClick={handleForceDeleteConfirm} onCancelClick={() => setShowForceDeleteModal(false)}
        positiveLabel={t('common.delete_button')} negativeLabel={t('common.cancel_button')}
        positiveButtonProps={{ className: 'bg-red-600 hover:bg-red-700' }}
      >
         <div className="flex items-center"><FontAwesomeIcon icon={faTrashAlt} className="text-red-600 text-xl mr-3" /><p>Copies will not be deleted!</p></div>
      </CBModal>
      <CBModal
        title={t('organization.org_sync_resync_confirm_title')} visible={showResyncModal}
        onPositiveClick={handleResyncConfirm} onCancelClick={() => setShowResyncModal(false)}
        positiveLabel={t('organization.org_sync_resync_button')} negativeLabel={t('common.cancel_button')}
        // okType='danger' // Maybe not danger?
      >
         <div className="flex items-center"><p>Resync this connection?</p></div>
      </CBModal>
       <CBModal
        title={t('organization.org_sync_repair_confirm_title')} visible={showRepairModal}
        onPositiveClick={handleRepairConfirm} onCancelClick={() => setShowRepairModal(false)}
        positiveLabel={t('organization.org_sync_repair_button')} negativeLabel={t('common.cancel_button')}
        // okType='danger'
      >
         <div className="flex items-center"><p>Attempt to repair this stuck sync?</p></div>
      </CBModal>
    </>
  )
}

export default SyncItem;
